import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function GalleryLightbox({ images = [], index, onClose }) {
  const [cur, setCur] = useState(index || 0);
  const touchX = useRef(null);
  const open = index != null;

  useEffect(() => { if (index != null) setCur(index); }, [index]);

  const go = useCallback((dir) => {
    setCur((c) => Math.min(images.length - 1, Math.max(0, c + dir)));
  }, [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, go, onClose]);

  if (!open) return null;
  const img = images[cur] || {};

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-black flex flex-col select-none" data-testid="rider-lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-14 shrink-0">
        <span className="text-white/70 text-sm font-medium tabular-nums" data-testid="rider-lightbox-counter">{cur + 1} / {images.length}</span>
        <button onClick={onClose} data-testid="rider-lightbox-close" aria-label="Close"
          className="w-9 h-9 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white active:scale-95"><X className="w-5 h-5" /></button>
      </div>

      {/* Image stage */}
      <div className="flex-1 min-h-0 relative flex items-center justify-center px-2" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <img key={cur} src={img.url} alt={img.caption || ""} data-testid="rider-lightbox-image"
          className="max-h-full max-w-full object-contain rounded-lg animate-[fadeIn_0.2s_ease]" draggable={false} />

        {cur > 0 && (
          <button onClick={() => go(-1)} data-testid="rider-lightbox-prev" aria-label="Previous"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white active:scale-95"><ChevronLeft className="w-6 h-6" /></button>
        )}
        {cur < images.length - 1 && (
          <button onClick={() => go(1)} data-testid="rider-lightbox-next" aria-label="Next"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white active:scale-95"><ChevronRight className="w-6 h-6" /></button>
        )}
      </div>

      {/* Caption + dots */}
      <div className="shrink-0 px-5 pb-6 pt-3 text-center">
        {img.caption && <p className="text-white/90 text-sm" data-testid="rider-lightbox-caption">{img.caption}</p>}
        {images.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {images.map((_, i) => (
              <button key={i} onClick={() => setCur(i)} aria-label={`Go to image ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === cur ? "w-5 bg-white" : "w-1.5 bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
